import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from '../Toast';
import { MessageCircle, Send, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { authAxios } from '../../services/crmApi';

interface PortalMessage {
  id: number;
  sender_type: 'client' | 'staff' | 'operator';
  message: string;
  is_read: boolean;
  created_at: string;
}

interface Props {
  applicationId: number;
  clientName?: string;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'teraz';
  if (mins < 60) return `${mins} min temu`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h temu`;
  return `${Math.floor(hrs / 24)}d temu`;
}

export const PortalChatPanel: React.FC<Props> = ({ applicationId, clientName }) => {
  const [messages, setMessages] = useState<PortalMessage[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`/api/operator/portal-messages/${applicationId}`);
      setMessages(data);
      setUnread(0);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [applicationId]);

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`/api/operator/portal-messages-unread/${applicationId}`);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, [applicationId]);

  useEffect(() => { loadUnread(); }, [loadUnread]);
  useEffect(() => { if (expanded) load(); }, [expanded, load]);

  useEffect(() => {
    if (expanded && endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, expanded]);

  useEffect(() => {
    if (!expanded) return;
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [expanded, load]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await authAxios.post(`/api/operator/portal-messages/${applicationId}`, { message: text.trim() });
      setText('');
      await load();
    } catch { toast.error('Błąd wysyłania'); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2">
          <MessageCircle size={18} className="text-emerald-600" />
          <span className="font-bold text-sm">
            Portal Klienta
            {clientName && <span className="font-normal text-gray-500 ml-1">({clientName})</span>}
          </span>
          {unread > 0 && (
            <span className="bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">
              {unread}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          <div className="max-h-72 overflow-y-auto p-3 space-y-2 bg-gray-50">
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-gray-400" /></div>
            ) : messages.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-xs">
                <MessageCircle size={24} className="mx-auto mb-2 opacity-40" />
                Brak wiadomości z portalu klienta
              </div>
            ) : (
              messages.map(msg => {
                const isOp = msg.sender_type !== 'client';
                return (
                  <div key={msg.id} className={`flex ${isOp ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-xl px-3 py-2 ${
                      isOp ? 'bg-emerald-600 text-white rounded-br-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                    }`}>
                      <p className="text-xs whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-[10px] mt-1 ${isOp ? 'text-emerald-200' : 'text-gray-400'}`}>
                        {isOp ? 'Ty' : clientName || 'Klient'} &middot; {timeAgo(msg.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-gray-100 flex gap-2 items-end">
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              rows={2}
              placeholder="Odpowiedz klientowi z portalu..."
              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 resize-none"
            />
            <button
              onClick={handleSend}
              disabled={!text.trim() || sending}
              className="shrink-0 w-9 h-9 bg-emerald-600 text-white rounded-lg flex items-center justify-center hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
